

import React, {
  useContext, lazy,
  forwardRef, useImperativeHandle,
} from 'react';
import {
  Route,
  Redirect,
  Switch,
} from 'react-router-dom';
import InitLoading from '@/components/InitLoading/initLoading';
import Loading from '@/components/Loading/loading';
import callEngine from '@/3DEngine/callEngine/index';
import { getJsonTemplate, jsonTemplateUpdate } from '@/apis/size';
import {
  cabinetShape, cabinetType1, HighCabinetId,
} from '@/utils/rules/const';
import StepOne from '@/components/sizeData/components/stepOne';
import { AppContext } from '../../redux/store';
import sizeR from './SizeData.module.scss';

const StepTwo = lazy(() => import('@/components/sizeData/components/stepTwo'));
const StepThree = lazy(() => import('@/components/sizeData/components/stepThree'));

function SizeSelection(props, ref) {
  const {
    typeI, typeH, typeL,
  } = cabinetShape;

  const { initState, initState: { isSeat }, dispatch } = useContext(AppContext);


  /**
   * @description: 底柜长度对应字段
   * @param {*}: type => 柜型  lens => 当前尺寸集合
   * @return {*}: object
   */
  const bottomLenParameter = (type, lens) => {
    if (type === typeI) {
      return {
        len1: lens[0],
      };
    }
    if (type === typeL) {
      return {
        len1: lens[0],
        len2: lens[1],
      };
    }
    if (type === typeH) {
      return {
        len1: lens[0],
        len3: lens[2] ? lens[2] : lens[1],
      };
    }
    return {
      len1: lens[0],
      len2: lens[1],
      len3: lens[2],
    };
  };

  /**
   * @description: 顶柜长度对应字段
   * @param {*}: type => 柜型  lens => 当前尺寸集合
   * @return {*}: object
   */
  const topLenParameter = (type, lens) => {
    if (type === typeI) {
      if (isSeat === 'R') {
        return {
          len1: lens[0] ? lens[0] : lens[1],
        };
      }
      if (isSeat === 'M') {
        return {
          len2: lens[1] ? lens[1] : lens.filter((n) => n)[0],
        };
      }
      if (isSeat === 'L') {
        if (initState.sizeUnder.key === 'L') {
          return { len2: lens[0] };
        }
        return { len3: lens.filter((n) => n)[0] };
      }
    }
    if (type === typeH) {
      return {
        len1: lens[0],
        len3: lens[1] ? lens[1] : lens[2],
      };
    }
    if (isSeat === 'R') {
      return {
        len1: lens[0],
        len2: lens[1],
      };
    }
    if (isSeat === 'L') {
      return {
        len2: lens[1],
        len3: lens[2] ? lens[2] : lens[0],
      };
    }
    return {};
  };

  // 顶柜对应高度
  const topHigh = {
    1: 60,
    2: 80,
  };
  // 底柜对应高度
  const bottomHigh = {
    1: 70,
    2: 80,
  };

  // 3d引擎渲染
  const getCallEngine = (res) => {
    if (initState.engine3D && res?.bottom) {
      // Loading.show();
      initState.engine3D.isRendering = false;
      callEngine(initState.engine3D, 'init', undefined, res).then((reload) => {
        if (reload?.calloutLines) {
          dispatch({ type: 'calloutline', payload: reload.calloutLines });
        }
        if (reload?.existWindow && !initState.isWindow) { // 如果原本是没有窗户，但是切换模型后窗户打开，那么需要再次把窗户关了
          callEngine(initState.engine3D, 'window', false, undefined);
        }
        dispatch({ type: 'existWindow', payload: reload.existWindow });
        // Loading.hide();
      }).finally(() => {
        dispatch({ type: 'showLoading', payload: false });
        dispatch({ type: 'is3DFirstInit', payload: true });// 第一次调用callEngine的init结束设为true，用来确定3D手势提示显示的时机
        Loading.hide();
        dispatch({ type: 'showInitLoading', payload: false });
      });
    } else {
      Loading.hide();
      dispatch({ type: 'showInitLoading', payload: false });
    }
  };

  // 初始化jsonTemplate时保存水龙头的faucetComponentId
  const getFaucetComponent = (data) => {
    const { accessories } = data;
    const faucet = accessories?.filter((i) => i.kitchenCategoryId === cabinetType1.XXV)[0]?.frameId;
    dispatch({ type: 'faucetComponentId', payload: faucet });
  };

  // 初始化templateJson
  const {
    sizeUnder, sizeTop, lengthVal, lengthTopVal,
    showInitLoading,
  } = initState;
  // 参数
  const parameter = {
    bottom: {
      type: sizeUnder.key,
      height: bottomHigh[initState.highUnder],
      sinkPosition: initState.sizeUnder.key === typeH && +initState.location.sinkLocation === 2 ? 3
        : initState.location.sinkLocation,
      stovePosition: initState.sizeUnder.key === typeH && +initState.location.cookLocation === 2 ? 3
        : initState.location.cookLocation,
      fridgeType: initState.refrigerId,
      highAndLow: initState.isHighlowCabinet,
      ...bottomLenParameter(sizeUnder.key, lengthVal),
    },
    top: initState.hasTopCabinet ? {
      type: sizeTop.key,
      height: topHigh[initState.TophighUnder],
      ...topLenParameter(sizeTop.key, lengthTopVal),
    } : null,
  };

  // Apiaction/init/update
  const Apiaction = async (params = parameter) => {
    if (initState.Modify) { // 是否修改过
      if (!showInitLoading) Loading.show();
      const oldTemjson = () => {
        if (initState.arrowShow) {
          return initState.jsonTemplate; // 非修改页面
        }
        if (Object.keys(initState.oldTempleteJson).length === 0) {
          return initState.jsonTemplate; // 修改第一次取jsonTemplate，否则下面oldTempleteJson
        }
        return initState.oldTempleteJson;
      };
      const obj = {
        mccInit: params,
        jsonVal: oldTemjson(),
      };
      const res = await jsonTemplateUpdate(obj); // update接口
      const detailedList = res.data.data.componentPriceVO;
      if (initState.arrowShow) {
        dispatch({ type: 'getStepDetail', payload: detailedList });
        getFaucetComponent(detailedList); // 初始化jsonTemplate时保存水龙头的faucetComponentId
      }
      // 高柜信息
      const highCabinet = detailedList?.accessories?.filter((it) => HighCabinetId.includes(it.kitchenCategoryId));
      dispatch({ type: 'tallark-detail', payload: highCabinet.map((it) => ({ ...it, id: it.kitchenCategoryId })) });

      // 存放更改时候第一次的templeteJson
      if (!initState.arrowShow && Object.keys(initState.oldTempleteJson).length === 0) {
        dispatch({ type: 'oldTempleteJson', payload: res.data.data.oldMcc });
      }
      dispatch({ type: 'json-template', payload: res.data.data.newMcc }); // newTempleteJson
      getCallEngine(res.data.data.newMcc); // 3d引擎渲染
      const accessories = res?.data?.data?.componentPriceVO?.accessories;
      // 重置底柜内配
      const seasoning = accessories.filter((it) => it.kitchenCategoryId === cabinetType1.XIII);
      const underArr = [];
      if (seasoning.length) {
        underArr[0] = seasoning[0].quantity;
      } else {
        underArr[0] = 0;
      }
      const underBasket = accessories.filter((it) => cabinetType1.XVIII === it.kitchenCategoryId);
      if (underBasket.length) {
        underArr[1] = underBasket[0].quantity;
      } else {
        underArr[1] = 0;
      }
      dispatch({ type: 'basket-under-num', payload: underArr });
      // 重置顶柜内配
      const topBasket = accessories.filter((it) => cabinetType1.XIX === it.kitchenCategoryId);
      if (topBasket.length) {
        dispatch({ type: 'basket-top-num', payload: [topBasket[0].quantity] });
      } else {
        dispatch({ type: 'basket-top-num', payload: [0] });
      }
    } else {
      if (!showInitLoading) Loading.show();
      getJsonTemplate(params).then((res) => { // init接口
        // 存放更改时候第一次的templeteJson
        if (!initState.arrowShow && Object.keys(initState.oldTempleteJson).length === 0) {
          dispatch({ type: 'oldTempleteJson', payload: res?.data?.data?.mccInitVO });
        }
        dispatch({ type: 'json-template', payload: res?.data?.data?.mccInitVO }); // 获取初始化TEMPLATE-JSON
        dispatch({ type: 'getStepDetail', payload: res?.data?.data?.componentPriceVO }); // 获取初始化清单
        getCallEngine(res?.data?.data?.mccInitVO); // 3d引擎渲染
        getFaucetComponent(res?.data?.data?.componentPriceVO);// 初始化jsonTemplate时保存水龙头的faucetComponentId
      }).catch(() => {
        Loading.hide();
        dispatch({ type: 'showInitLoading', payload: false });
      });
      initState.underBaskNum = [];
    }
  };

  useImperativeHandle(ref, () => ({
    Apiaction, // 父级调用子组件Apiaction
  }));

  // 切换Two
  const getTwoAction = (param, type) => {
    let obj;
    switch (type) {
      case 'top': // 顶柜高度
        obj = { ...parameter, top: { ...parameter.top, ...param } };
        break;
      case 'bottom': // 底柜高度
        obj = { ...parameter, bottom: { ...parameter.bottom, ...param } };
        break;
      case 'topSize': // 顶柜尺寸
        obj = { ...parameter, top: { ...parameter.top, ...topLenParameter(sizeTop.key, param) } };
        break;
      case 'bottomSize': // 底柜尺寸
        if (isSeat === 'M' && param[1] < parameter.top?.len2) {
          // U型底柜，I型顶柜在中间，如果底柜L2小于，顶柜长度，需要一并更改顶柜长度等于底柜L2
          obj = {
            ...parameter,
            top: { ...parameter.top, ...topLenParameter(sizeTop.key, [param[1]]) },
            bottom: {
              ...parameter.bottom,
              ...bottomLenParameter(sizeUnder.key, param),
              sinkPosition: null,
              stovePosition: null,
            },
          };
        } else {
          obj = {
            ...parameter,
            bottom: {
              ...parameter.bottom,
              ...bottomLenParameter(sizeUnder.key, param),
              sinkPosition: null,
              stovePosition: null,
            },
          };
        }

        break;
      default: // 上下尺寸都变
        obj = {
          ...parameter,
          top: { ...parameter.top, ...topLenParameter(sizeTop.key, param[0]) },
          bottom: {
            ...parameter.bottom,
            ...bottomLenParameter(sizeUnder.key, param[1]),
            sinkPosition: null,
            stovePosition: null,
          },
        };
    }
    Apiaction(obj);
  };

  // 切换Three
  const getThreeAction = (param) => {
    const obj = { ...parameter, bottom: { ...parameter.bottom, ...param } };
    Apiaction(obj);
  };


  return (

    <div className={sizeR.content}>
      <Switch>
        <Route path="/home/homepage/stepone" component={StepOne} />
        <Route path="/home/homepage/steptwo" render={() => <StepTwo getApiaction={getTwoAction} />} />
        <Route path="/home/homepage/stepthree" render={() => <StepThree getApiaction={getThreeAction} />} />
        <Route
          path="/home/homepage"
          render={() => <Redirect to="/home/homepage/stepone" />}
        />
      </Switch>

      {
        showInitLoading ? (
          <InitLoading style={{ width: '8.75', height: '8.75' }} />
        ) : null
      }

    </div>
  );
}

export default forwardRef(SizeSelection);

